/* Content-editor sections canvas (spec 02, phase 1). Six section types, */
/* TipTap-JSON rich text, autosave debounce (~600ms) + flush, image upload, */
/* live bareme total. Read-only when the document is archived. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import WidgetFrame from './widget-frame';
import { RichDocEditor, EMPTY_RICH_DOC, markdownToRichDoc, type RichDoc } from './rich-doc-editor';
import type { SectionUpdatedEvent, SectionCreatedEvent, SectionsReorderedEvent } from '../lib/chat-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ArrowUp, ArrowDown, Trash, Plus, X, FileText, Code, Eye, MagnifyingGlassPlus } from '@phosphor-icons/react';
import { LatexInline } from './math-node-views';
import { DocumentScalesContext, GradingField, gradingOf } from './grading-field';
import { scalesOfSections, type SectionGrading } from '../lib/grading-scale';

export type ContentSectionType = 'text' | 'table' | 'chart' | 'qcm' | 'image' | 'question_with_answer';

export interface SectionsRepository {
  load(): Promise<ContentSectionRow[]>;
  upsert(patch: { id?: string; updated_at?: string; position: number; type: ContentSectionType; title: string | null; content: unknown }): Promise<ContentSectionRow>;
  delete(id: string): Promise<void>;
  reorder(orderedIds: string[]): Promise<void>;
}

export interface SectionConflict { content: unknown; title: string | null }

const isConflictError = (e: unknown) =>
  (e as { data?: { code?: string } } | null)?.data?.code === 'CONFLICT';

export function trpcSectionsRepository(documentId: string): SectionsRepository {
  return {
    load: async () => {
      const data = await api.content.get.query({ id: documentId });
      return (data?.sections ?? []) as ContentSectionRow[];
    },
    upsert: async (patch) => api.content.sections.upsert.mutate({ ...patch, documentId }) as Promise<ContentSectionRow>,
    delete: async (id) => { await api.content.sections.delete.mutate({ id }); },
    reorder: async (orderedIds) => { await api.content.sections.reorder.mutate({ documentId, orderedIds }); },
  };
}

/* Lot 2 (spec 13) — édition par onglet de variante. `variantKey` absent/null
   = variante de référence, comportement STRICTEMENT identique à
   `trpcSectionsRepository` (aucune fourche de comportement pour les
   documents sans variantes). `variantKey` posé : `load()` fusionne le
   contenu de cette variante sur les sections qui la couvrent (les autres
   restent sur leur contenu de référence, partagé) ; `upsert` route le
   contenu vers `sections.upsertVariant` (jamais `sections.upsert`, qui
   créerait/déplacerait des sections — interdit pour une variante) ;
   `delete`/`reorder` sont des no-op : une variante ne change jamais la
   structure du document. */
export function variantAwareSectionsRepository(documentId: string, variantKey: string | null): SectionsRepository {
  const base = trpcSectionsRepository(documentId);
  if (!variantKey) return base;
  return {
    load: async () => {
      const [sections, variants] = await Promise.all([
        base.load(),
        api.content.sections.variantsByDocument.query({ documentId }) as Promise<{ section_id: string; variant_key: string; content: unknown }[]>,
      ]);
      const byId = new Map(variants.filter((v) => v.variant_key === variantKey).map((v) => [v.section_id, v.content]));
      /* Fusion sur la référence, JAMAIS un remplacement : le bareme n'est
       * volontairement jamais dupliqué par variante (partagé, design figé —
       * content_section_variants n'en porte pas). Un remplacement intégral
       * de `content` par celui de la variante perd donc silencieusement
       * "bareme" (et tout autre champ que la variante ne porte pas), d'où
       * l'affichage "— pts" en variante 2 (retour Ismaël, staging, lot 4).
       * Même règle de fusion que côté serveur pour la correction
       * (exam-services.ts). */
      return sections.map((s) => {
        const override = byId.get(s.id);
        if (!override || typeof override !== 'object' || typeof s.content !== 'object' || s.content === null) return s;
        return { ...s, content: { ...s.content, ...override } };
      });
    },
    upsert: async (patch) => {
      if (!patch.id) throw new Error('Une variante ne peut pas créer de nouvelle section.');
      /* `patch.content` vient de l'état local, construit par fusion sur la
       * référence (voir `load` ci-dessus) — il PORTE "bareme" à ce stade.
       * Ne jamais le réécrire dans content_section_variants (design figé :
       * le bareme est exclusivement porté par la référence), sous peine de
       * dupliquer silencieusement une valeur qui pourrait diverger d'une
       * future édition du bareme de référence. */
      let variantContent = patch.content;
      if (variantContent && typeof variantContent === 'object' && !Array.isArray(variantContent)) {
        variantContent = Object.fromEntries(
          Object.entries(variantContent as Record<string, unknown>).filter(([key]) => key !== 'bareme'),
        );
      }
      await api.content.sections.upsertVariant.mutate({ sectionId: patch.id, variantKey, content: variantContent });
      return { id: patch.id, position: patch.position, type: patch.type, title: patch.title, content: patch.content };
    },
    delete: async () => {},
    reorder: async () => {},
  };
}

export function mockSectionsRepository(): SectionsRepository {
  return {
    load: async () => [],
    upsert: async (patch) => ({ id: patch.id ?? uid(), position: patch.position, type: patch.type, title: patch.title, content: patch.content }),
    delete: async () => {},
    reorder: async () => {},
  };
}

export interface ContentSectionRow {
  id: string;
  document_id?: string;
  position: number;
  type: string;
  title: string | null;
  content?: unknown;
  updated_at?: string;
}

interface TextC { body: RichDoc | null }
interface TableC { caption?: string | null; headers: string[]; rows: string[][] }
interface ChartC { html: string; title?: string | null }
interface Choice { id: string; label: RichDoc }
interface QcmC { prompt: RichDoc | null; choices: Choice[]; answer: { correct_choice_ids: string[] } | null; correction_rules: RichDoc | null; bareme: number | null; grading?: SectionGrading }
interface ImageC { storage_path: string; alt?: string | null; caption?: RichDoc | null }
interface QwaC { prompt: RichDoc | null; answer: RichDoc | null; correction_rules: RichDoc | null; bareme: number | null; grading?: SectionGrading }

const TYPE_LABELS: Record<ContentSectionType, string> = {
  text: 'Texte', table: 'Tableau', chart: 'Graphique', qcm: 'QCM', image: 'Image', question_with_answer: 'Question + réponse',
};
const TYPE_BADGE_LABELS: Record<ContentSectionType, string> = {
  text: 'Texte', table: 'Tableau', chart: 'Graphique', qcm: 'QCM', image: 'Image', question_with_answer: 'Question',
};
const TYPE_BADGE_CLASS: Record<ContentSectionType, string> = {
  text: 'bg-[var(--naia-sky)] text-naia-blue',
  qcm: 'bg-[rgba(124,58,237,0.1)] text-[#7c3aed]',
  question_with_answer: 'bg-[rgba(217,119,6,0.1)] text-[#b45309]',
  table: 'bg-[rgba(20,184,166,0.1)] text-[#0f766e]',
  image: 'bg-[rgba(236,72,153,0.1)] text-[#be185d]',
  chart: 'bg-[rgba(99,102,241,0.1)] text-[#4338ca]',
};
const TYPE_ACCENT: Record<ContentSectionType, string> = {
  text: 'border-l-naia-blue',
  qcm: 'border-l-[#7c3aed]',
  question_with_answer: 'border-l-[#b45309]',
  table: 'border-l-[#0f766e]',
  image: 'border-l-[#be185d]',
  chart: 'border-l-[#4338ca]',
};
const TYPE_LIST: ContentSectionType[] = ['text', 'table', 'chart', 'qcm', 'image', 'question_with_answer'];

const uid = () => Math.random().toString(36).slice(2, 10);
const textToDoc = (s: string): RichDoc => ({ type: 'doc', content: [{ type: 'paragraph', content: s ? [{ type: 'text', text: s }] : [] }] });

function defaultContent(type: ContentSectionType): unknown {
  if (type === 'text') return { body: EMPTY_RICH_DOC };
  if (type === 'table') return { caption: null, headers: ['Colonne 1', 'Colonne 2'], rows: [['', '']] };
  if (type === 'chart') return { html: '<canvas id="c"></canvas>\n<script>\n/* D3 / Chart.js */\n</script>', title: null };
  if (type === 'qcm') return { prompt: EMPTY_RICH_DOC, choices: [{ id: uid(), label: textToDoc('') }, { id: uid(), label: textToDoc('') }], answer: { correct_choice_ids: [] }, correction_rules: null, bareme: null };
  if (type === 'image') return { storage_path: '', alt: null, caption: null };
  return { prompt: EMPTY_RICH_DOC, answer: null, correction_rules: null, bareme: null };
}


const fieldClass = "flex flex-col gap-1";
const labelClass = "text-[11px] font-bold text-muted-foreground tracking-[0.5px] uppercase";
const stackClass = "flex flex-col gap-2";

/* ── Per-type editors ─────────────────────────────────────────────────────────── */

function TextEditor({ content, onChange, editable }: { content: TextC & { markdown?: string }; onChange: (c: unknown) => void; editable: boolean }) {
  /* Legacy 'cours' text sections stored `{ markdown }`; migrate to RichDoc on load. */
  const body = useMemo(
    () => content?.body ?? (typeof content?.markdown === 'string' ? markdownToRichDoc(content.markdown) : null),
    [content?.body, content?.markdown],
  );
  /* Les autres cles sont conservees : une section mal typee peut porter un
     contenu (html) que cet editeur ne sait pas afficher, et qu'il ne doit
     surtout pas effacer en le reecrivant. */
  return <RichDocEditor value={body} editable={editable} onChange={(next) => onChange({ ...(content ?? {}), body: next })} minHeight={160} />;
}

function TableEditor({ content, onChange, editable }: { content: TableC; onChange: (c: unknown) => void; editable: boolean }) {
  const headers: string[] = content?.headers ?? [];
  const rows: string[][] = content?.rows ?? [];
  const setHeader = (i: number, v: string) => { const h = [...headers]; h[i] = v; onChange({ ...content, headers: h }); };
  const setCell = (r: number, c: number, v: string) => { const rs = rows.map((x) => [...x]); rs[r]![c] = v; onChange({ ...content, rows: rs }); };
  const addRow = () => onChange({ ...content, rows: [...rows, headers.map(() => '')] });
  const addCol = () => onChange({ ...content, headers: [...headers, `Col ${headers.length + 1}`], rows: rows.map((r) => [...r, '']) });
  const delRow = (i: number) => onChange({ ...content, rows: rows.filter((_, ri) => ri !== i) });
  const delCol = (i: number) => onChange({ ...content, headers: headers.filter((_, ci) => ci !== i), rows: rows.map((r) => r.filter((_, ci) => ci !== i)) });
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse min-w-full text-[13px]">
        <thead><tr>
          {headers.map((h, i) => (
            <th key={i} className="p-1 border-b-2 border-border text-left">
              {editable ? (
                <div className="flex gap-1">
                  <Input value={h} onChange={(e) => setHeader(i, e.target.value)} className="font-bold py-[6px] px-2 text-[12px]" />
                  {headers.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => delCol(i)} className="w-7 h-7 min-h-0"><X size={12} /></Button>}
                </div>
              ) : (
                <span className="font-bold px-2 py-1 block"><LatexInline text={h} /></span>
              )}
            </th>
          ))}
          {editable && <th className="p-1 border-b-2 border-border"><Button type="button" variant="outline" onClick={addCol} className="text-[12px] min-h-0 py-[4px] px-[8px] h-auto">+col</Button></th>}
        </tr></thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c} className="p-1 border-b border-border">
                  {editable ? (
                    <Input value={cell} onChange={(e) => setCell(r, c, e.target.value)} className="py-[6px] px-2 text-[12px]" />
                  ) : (
                    <span className="px-2 py-1 block"><LatexInline text={cell} /></span>
                  )}
                </td>
              ))}
              {editable && <td className="p-1"><Button type="button" variant="ghost" size="icon" onClick={() => delRow(r)} className="w-7 h-7 min-h-0"><X size={12} /></Button></td>}
            </tr>
          ))}
        </tbody>
      </table>
      {editable && <Button type="button" variant="outline" onClick={addRow} className="mt-[6px] text-[12px] min-h-0 py-[4px] px-[8px] h-auto">+ ligne</Button>}
    </div>
  );
}

function ChartEditor({ content, onChange, editable, showControls }: { content: ChartC; onChange: (c: unknown) => void; editable: boolean; showControls?: boolean }) {
  const [preview, setPreview] = useState(true);
  const iconBtn = (active: boolean, icon: React.ReactNode, title: string, onClick: () => void) => (
    <button type="button" title={title} onClick={onClick}
      className={cn("w-7 h-7 rounded flex items-center justify-center transition-colors border-none cursor-pointer font-[inherit]",
        active ? "bg-naia-blue text-white" : "bg-transparent text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]"
      )}>
      {icon}
    </button>
  );
  return (
    <div>
      {(showControls || !preview) && (
        <div className="flex gap-0.5 mb-[6px] justify-end">
          {iconBtn(!preview, <Code size={14} />, 'Code HTML', () => setPreview(false))}
          {iconBtn(preview, <Eye size={14} />, 'Aperçu', () => setPreview(true))}
        </div>
      )}
      {preview ? (
        <WidgetFrame html={content?.html ?? ''} title={content?.title ?? undefined} height={280} />
      ) : (
        <textarea value={content?.html ?? ''} disabled={!editable} onChange={(e) => onChange({ ...content, html: e.target.value })} rows={10} placeholder="HTML + JS (D3, Chart.js)…" className="w-full p-[10px_12px] rounded-sm border-[1.5px] border-naia-muted-2 font-mono text-[12px] resize-y bg-background text-foreground outline-none box-border" />
      )}
    </div>
  );
}

function QcmEditor({ content, onChange, editable, baremeEditable = editable }: { content: QcmC; onChange: (c: unknown) => void; editable: boolean; baremeEditable?: boolean }) {
  const choices: Choice[] = content?.choices ?? [];
  const correct: string[] = content?.answer?.correct_choice_ids ?? [];
  const setChoiceLabel = (id: string, label: RichDoc) => onChange({ ...content, choices: choices.map((c) => c.id === id ? { ...c, label } : c) });
  const toggleCorrect = (id: string) => onChange({ ...content, answer: { correct_choice_ids: correct.includes(id) ? correct.filter((x) => x !== id) : [...correct, id] } });
  const addChoice = () => onChange({ ...content, choices: [...choices, { id: uid(), label: textToDoc('') }] });
  const delChoice = (id: string) => onChange({ ...content, choices: choices.filter((c) => c.id !== id), answer: { correct_choice_ids: correct.filter((x) => x !== id) } });
  return (
    <div className={stackClass}>
      <RichDocEditor value={content?.prompt ?? null} editable={editable} onChange={(prompt) => onChange({ ...content, prompt })} minHeight={70} />
      <div className="flex flex-col gap-1">
        {choices.map((ch) => (
          <div key={ch.id} className="flex gap-2 items-center">
            <input type="checkbox" disabled={!editable} checked={correct.includes(ch.id)} onChange={() => toggleCorrect(ch.id)} className="flex-shrink-0 w-4 h-4 accent-naia-blue" />
            <div className="flex-1 min-w-0"><RichDocEditor value={(ch.label as RichDoc | null) ?? null} editable={editable} singleLine minHeight={32} onChange={(label) => setChoiceLabel(ch.id, label)} /></div>
            {editable && choices.length > 2 && <Button type="button" variant="ghost" size="icon" onClick={() => delChoice(ch.id)} className="w-7 h-7 min-h-0"><X size={12} /></Button>}
          </div>
        ))}
        {editable && choices.length < 8 && <Button type="button" variant="outline" onClick={addChoice} className="self-start text-[12px] min-h-0 py-[3px] px-[8px] h-auto mt-0.5">+ proposition</Button>}
      </div>
      <RichDocEditor value={content?.correction_rules ?? null} editable={editable} onChange={(correction_rules) => onChange({ ...content, correction_rules })} minHeight={50} />
      <GradingField bareme={content?.bareme ?? null} grading={gradingOf(content)} editable={baremeEditable} onBareme={(bareme) => onChange({ ...content, bareme })} onGrading={(grading) => onChange({ ...content, grading })} />
    </div>
  );
}

function QuestionWithAnswerEditor({ content, onChange, editable, baremeEditable = editable }: { content: QwaC; onChange: (c: unknown) => void; editable: boolean; baremeEditable?: boolean }) {
  return (
    <div className={stackClass}>
      <RichDocEditor value={content?.prompt ?? null} editable={editable} onChange={(prompt) => onChange({ ...content, prompt })} minHeight={70} />
      <div className={fieldClass}><label className={labelClass}>Corrigé</label><RichDocEditor value={content?.answer ?? null} editable={editable} onChange={(answer) => onChange({ ...content, answer })} minHeight={70} /></div>
      <RichDocEditor value={content?.correction_rules ?? null} editable={editable} onChange={(correction_rules) => onChange({ ...content, correction_rules })} minHeight={50} />
      <GradingField bareme={content?.bareme ?? null} grading={gradingOf(content)} editable={baremeEditable} onBareme={(bareme) => onChange({ ...content, bareme })} onGrading={(grading) => onChange({ ...content, grading })} />
    </div>
  );
}

function ImageEditor({ documentId, content, onChange, editable }: { documentId: string; content: ImageC; onChange: (c: unknown) => void; editable: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const path: string = content?.storage_path ?? '';

  useEffect(() => {
    let cancelled = false;
    if (!path) { setUrl(null); return; }
    api.content.imageReadUrl.query({ path }).then((u) => { if (!cancelled) setUrl(u); }).catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [path]);

  const onFile = async (file: File) => {
    setUploading(true); setErr(null);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const { path: p, signedUrl } = await api.content.imageUploadUrl.mutate({ documentId, ext });
      const put = await fetch(signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      if (!put.ok) throw new Error(`Upload échoué (${put.status})`);
      onChange({ ...content, storage_path: p });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className={stackClass}>
      {url ? (
        <>
          <button
            type="button"
            onClick={() => setZoomed(true)}
            className="relative self-start cursor-zoom-in border-none bg-transparent p-0"
            aria-label="Agrandir le visuel"
          >
            <img src={url} alt={content?.alt ?? ''} className="max-w-full rounded-sm border border-border" />
            <span className="absolute bottom-2 right-2 flex items-center justify-center rounded-full bg-black/55 p-1.5 text-white">
              <MagnifyingGlassPlus size={16} weight="bold" />
            </span>
          </button>
          <Dialog open={zoomed} onOpenChange={setZoomed}>
            <DialogContent
              aria-label={content?.alt ? `Visuel agrandi : ${content.alt}` : 'Visuel agrandi'}
              style={{ width: 'auto', maxWidth: '95vw', maxHeight: '95vh', padding: 8, background: 'var(--naia-card)' }}
            >
              <img src={url} alt={content?.alt ?? ''} className="max-h-[90vh] max-w-[93vw] rounded-sm object-contain" />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="p-6 text-center text-muted-foreground border-[1.5px] border-dashed border-border rounded-sm text-[13px]">{path ? "Chargement de l'image…" : 'Aucune image'}</div>
      )}
      {editable && (
        <label
          className="self-start bg-naia-card-alt text-muted-foreground border border-border rounded-[6px] font-bold text-[12px] p-[6px_12px] font-[inherit]"
          style={{ cursor: uploading ? 'wait' : 'pointer' }}
        >
          {uploading ? "Téléversement…" : (path ? "Remplacer l'image" : "Téléverser une image")}
          <input type="file" accept="image/*" hidden disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }} />
        </label>
      )}
      {err && <span className="text-naia-error text-[12px]">{err}</span>}
      <Input value={content?.alt ?? ''} disabled={!editable} onChange={(e) => onChange({ ...content, alt: e.target.value || null })} placeholder="Texte alternatif…" />
    </div>
  );
}

/* ── Add-section helpers ──────────────────────────────────────────────────────── */

function TypePickerRow({ onSelect, onCancel }: { onSelect: (t: ContentSectionType) => void; onCancel: () => void }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap px-[14px] py-[8px] bg-[var(--naia-card-alt)] border border-[var(--naia-line)] rounded-md mt-2">
      {TYPE_LIST.map((t) => (
        <button
          key={t} type="button" onClick={() => onSelect(t)}
          className={cn("text-[11px] font-bold px-[9px] py-[4px] rounded-full transition-opacity hover:opacity-75 cursor-pointer border-none font-[inherit]", TYPE_BADGE_CLASS[t])}
        >
          {TYPE_LABELS[t]}
        </button>
      ))}
      <button type="button" onClick={onCancel} className="ml-auto text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)] transition-colors bg-transparent border-none cursor-pointer flex items-center">
        <X size={14} />
      </button>
    </div>
  );
}

function AddHandle({ onAdd, disabled }: { onAdd: () => void; disabled?: boolean }) {
  return (
    <button
      type="button" disabled={disabled} onClick={onAdd}
      className="w-full h-[28px] flex items-center gap-2 text-[11px] font-bold text-[var(--naia-text-secondary)] hover:text-naia-blue transition-colors group/add disabled:opacity-30 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer font-[inherit]"
    >
      <div className="flex-1 h-px bg-[var(--naia-line)] group-hover/add:bg-naia-blue/40 transition-colors" />
      <Plus size={11} weight="bold" className="shrink-0" />
      <span>Ajouter une section</span>
      <div className="flex-1 h-px bg-[var(--naia-line)] group-hover/add:bg-naia-blue/40 transition-colors" />
    </button>
  );
}

function ConflictBanner({ conflict, onReload }: { conflict: SectionConflict; onReload: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ title: conflict.title, content: conflict.content }, null, 2));
      setCopied(true);
    } catch { setCopied(false); }
  };
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 mb-1.5 text-[13px] text-[var(--naia-text-primary)]">
      <span>Vos dernières modifications ne sont pas enregistrées.</span>
      <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
        {copied ? 'Modifications copiées' : 'Copier mes modifications'}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onReload}>Recharger</Button>
    </div>
  );
}

/* ── Section row ──────────────────────────────────────────────────────────────── */

function SectionRow({
  section, documentId, idx, total, editable, structureEditable = true, onChange, onTitle, onDelete, onMove,
  onAdd, addDisabled, showTypePicker, onPickType, onCancelPick, conflict, onReload,
}: {
  conflict?: SectionConflict; onReload?: () => void;
  section: ContentSectionRow; documentId: string; idx: number; total: number; editable: boolean;
  /** Ajout/suppression/réordre/titre — propriétés PARTAGÉES entre variantes,
   *  jamais éditables depuis un onglet non-référence (lot 2, spec 13). */
  structureEditable?: boolean;
  onChange: (content: unknown) => void; onTitle: (t: string | null) => void; onDelete: () => void; onMove: (dir: -1 | 1) => void;
  onAdd?: () => void; addDisabled?: boolean; showTypePicker?: boolean;
  onPickType?: (t: ContentSectionType) => void; onCancelPick?: () => void;
}) {
  const content = (section.content ?? {}) as Record<string, unknown>;
  const [localTitle, setLocalTitle] = useState(section.title ?? '');
  const [titleFocused, setTitleFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [innerFocused, setInnerFocused] = useState(false);
  useEffect(() => setLocalTitle(section.title ?? ''), [section.title]);

  const type = section.type as ContentSectionType;
  const badgeClass = TYPE_BADGE_CLASS[type] ?? 'bg-muted text-muted-foreground';
  const accentBorder = TYPE_ACCENT[type] ?? 'border-l-[var(--naia-line)]';
  const showUI = hovered || innerFocused || titleFocused;

  return (
    <div
      className={cn(
        "border-l-[3px] pl-[10px] py-[6px] transition-colors duration-150",
        showUI ? accentBorder : "border-l-transparent",
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setInnerFocused(true)}
      onBlurCapture={() => setInnerFocused(false)}
    >
      <div className="flex items-center gap-1.5 mb-[5px] min-h-[22px]">
        <span className={cn(
          "text-[10px] rounded-full font-bold whitespace-nowrap shrink-0 overflow-hidden transition-all duration-150",
          badgeClass,
          showUI ? "px-[6px] py-[1px] opacity-100 max-w-[80px]" : "px-0 opacity-0 max-w-0",
        )}>
          {TYPE_BADGE_LABELS[type] ?? section.type}
        </span>
        {titleFocused || !localTitle ? (
          <input
            autoFocus={titleFocused}
            type="text"
            value={localTitle}
            disabled={!editable || !structureEditable}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => { e.stopPropagation(); setTitleFocused(true); }}
            onChange={(e) => setLocalTitle(e.target.value)}
            onBlur={() => {
              setTitleFocused(false);
              if (localTitle !== (section.title ?? '')) onTitle(localTitle || null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            placeholder="Titre de la section…"
            className="flex-1 min-w-0 bg-transparent border-0 border-b border-dashed border-[var(--naia-line)] font-semibold text-[13px] text-[var(--naia-text-primary)] placeholder:text-[var(--naia-text-secondary)] placeholder:font-normal px-1 py-0.5 outline-none font-[inherit] focus:border-naia-blue transition-colors"
          />
        ) : (
          <span
            onClick={(e) => { if (!editable || !structureEditable) return; e.stopPropagation(); setTitleFocused(true); }}
            className={cn('flex-1 min-w-0 font-bold text-[14px] text-[var(--naia-text-primary)] px-1 py-0.5 border-b border-transparent truncate', editable && structureEditable && 'cursor-text hover:border-dashed hover:border-[var(--naia-line)]')}
          >
            <LatexInline text={localTitle} />
          </span>
        )}
        <div
          className={cn("flex gap-0.5 flex-shrink-0 ml-auto transition-opacity duration-150", showUI ? "opacity-100" : "opacity-0 pointer-events-none")}
          onClick={(e) => e.stopPropagation()}
        >
          {editable && structureEditable && (
            <>
              <Button type="button" variant="ghost" size="icon-sm" disabled={idx === 0} onClick={() => onMove(-1)} className="text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]" title="Monter">
                <ArrowUp size={13} />
              </Button>
              <Button type="button" variant="ghost" size="icon-sm" disabled={idx === total - 1} onClick={() => onMove(1)} className="text-[var(--naia-text-secondary)] hover:text-[var(--naia-text-primary)]" title="Descendre">
                <ArrowDown size={13} />
              </Button>
              <Button type="button" variant="ghost" size="icon-sm" onClick={onDelete} className="text-[var(--naia-error)] hover:text-[var(--naia-error)] hover:bg-[rgba(231,50,50,0.08)]" title="Supprimer">
                <Trash size={13} />
              </Button>
            </>
          )}
        </div>
      </div>
      {conflict && <ConflictBanner conflict={conflict} onReload={() => onReload?.()} />}
      <div className="flex flex-col gap-1.5">
        {section.type === 'text' && <TextEditor content={content as unknown as TextC} editable={editable} onChange={onChange} />}
        {section.type === 'table' && <TableEditor content={content as unknown as TableC} editable={editable && showUI} onChange={onChange} />}
        {section.type === 'chart' && <ChartEditor content={content as unknown as ChartC} editable={editable} onChange={onChange} showControls={showUI} />}
        {section.type === 'qcm' && <QcmEditor content={content as unknown as QcmC} editable={editable && showUI} baremeEditable={editable && showUI && structureEditable} onChange={onChange} />}
        {section.type === 'image' && <ImageEditor documentId={documentId} content={content as unknown as ImageC} editable={editable && showUI} onChange={onChange} />}
        {section.type === 'question_with_answer' && <QuestionWithAnswerEditor content={content as unknown as QwaC} editable={editable && showUI} baremeEditable={editable && showUI && structureEditable} onChange={onChange} />}
      </div>
      {editable && structureEditable && onAdd && (showUI || showTypePicker) && (
        <div className="-ml-[14px] mt-1">
          {showTypePicker
            ? <TypePickerRow onSelect={onPickType!} onCancel={onCancelPick!} />
            : <AddHandle onAdd={onAdd} disabled={addDisabled} />
          }
        </div>
      )}
    </div>
  );
}

/* ── Editor ───────────────────────────────────────────────────────────────────── */

export interface ContentSectionsEditorProps {
  documentId: string;
  editable: boolean;
  /** Ajout/suppression/réordre/titre/barème — propriétés partagées entre
   *  variantes (lot 2, spec 13). `false` sur un onglet de variante
   *  non-référence ; par défaut égal à `editable` (comportement inchangé
   *  pour tout document sans variantes). */
  structureEditable?: boolean;
  repository?: SectionsRepository;
  initialSections?: ContentSectionRow[];
  incomingSection?: SectionUpdatedEvent | null;
  incomingCreated?: SectionCreatedEvent | null;
  incomingReordered?: SectionsReorderedEvent | null;
  /** Incrémenté par `resource_changed` (`resource==='content'`) sur CE document
   *  — déclenche un refetch ciblé via le même chemin que le chargement initial. */
  resourceChangedTick?: number;
  onSectionsChange?: (sections: ContentSectionRow[]) => void;
}

export default function ContentSectionsEditor({
  documentId, editable, structureEditable = editable, repository, initialSections, incomingSection, incomingCreated, incomingReordered, resourceChangedTick, onSectionsChange,
}: ContentSectionsEditorProps) {
  /* Recalculé quand `repository` change (bascule d'onglet de variante,
     lot 2) — pas figé au montage, sinon changer de variante n'aurait aucun
     effet sur ce que l'éditeur lit/écrit. */
  const repo = useMemo(() => repository ?? trpcSectionsRepository(documentId), [repository, documentId]);
  const hasInitial = initialSections !== undefined;
  const [sections, setSections] = useState<ContentSectionRow[]>(initialSections ?? []);
  const documentScales = useMemo(() => scalesOfSections(sections), [sections]);
  const [loading, setLoading] = useState(!hasInitial);
  const [error, setError] = useState<string | null>(null);
  const [addingType, setAddingType] = useState(false);
  const [insertAfterIdx, setInsertAfterIdx] = useState<number | null>(null);
  const [savingNew, setSavingNew] = useState(false);
  const [dirty, setDirty] = useState(false);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const versions = useRef<Record<string, string | undefined>>({});
  const saveChain = useRef<Record<string, Promise<void>>>({});
  const conflictIds = useRef<Set<string>>(new Set());
  const [conflicts, setConflicts] = useState<Record<string, SectionConflict>>({});

  const emit = useCallback((next: ContentSectionRow[]) => { onSectionsChange?.(next); return next; }, [onSectionsChange]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const loaded = await repo.load();
      versions.current = Object.fromEntries(loaded.map((s) => [s.id, s.updated_at]));
      setSections(emit(loaded));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [emit, repo]);

  useEffect(() => { if (!hasInitial) void load(); }, [load, hasInitial]);
  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);
  const prevTick = useRef<number | undefined>(resourceChangedTick);
  useEffect(() => {
    if (resourceChangedTick === undefined || resourceChangedTick === prevTick.current) return;
    prevTick.current = resourceChangedTick;
    void load();
  }, [resourceChangedTick, load]);

  const prevU = useRef<unknown>(null);
  useEffect(() => {
    if (!incomingSection || incomingSection === prevU.current) return;
    prevU.current = incomingSection;
    if (!timers.current[incomingSection.id] && incomingSection.updated_at) versions.current[incomingSection.id] = incomingSection.updated_at;
    setSections((prev) => emit(prev.map((s) => s.id === incomingSection.id ? { ...s, ...incomingSection } : s)));
  }, [incomingSection, emit]);
  const prevC = useRef<unknown>(null);
  useEffect(() => {
    if (!incomingCreated || incomingCreated === prevC.current) return;
    prevC.current = incomingCreated;
    if (incomingCreated.updated_at) versions.current[incomingCreated.id] = incomingCreated.updated_at;
    setSections((prev) => {
      if (prev.some((s) => s.id === incomingCreated.id)) return emit(prev.map((s) => s.id === incomingCreated.id ? { ...s, ...incomingCreated } : s));
      return emit([...prev, incomingCreated as ContentSectionRow].sort((a, b) => a.position - b.position));
    });
  }, [incomingCreated, emit]);
  const prevR = useRef<unknown>(null);
  useEffect(() => {
    if (!incomingReordered || incomingReordered === prevR.current) return;
    prevR.current = incomingReordered;
    for (const s of incomingReordered.sections as ContentSectionRow[]) {
      if (s.updated_at && !timers.current[s.id]) versions.current[s.id] = s.updated_at;
    }
    setSections(emit(incomingReordered.sections as ContentSectionRow[]));
  }, [incomingReordered, emit]);
  const persist = useCallback((section: ContentSectionRow, patch: { content?: unknown; title?: string | null }) => {
    const run = async () => {
      if (conflictIds.current.has(section.id)) return;
      setDirty(true);
      const title = patch.title !== undefined ? patch.title : section.title;
      const content = patch.content !== undefined ? patch.content : section.content;
      try {
        const updated = await repo.upsert({
          id: section.id, updated_at: versions.current[section.id], position: section.position,
          type: section.type as ContentSectionType, title, content,
        });
        if (updated.updated_at) versions.current[updated.id] = updated.updated_at;
        setSections((prev) => emit(prev.map((s) => {
          if (s.id !== updated.id) return s;
          return timers.current[s.id] ? { ...s, updated_at: updated.updated_at } : { ...updated };
        })));
      } catch (e) {
        if (isConflictError(e)) {
          conflictIds.current.add(section.id);
          clearTimeout(timers.current[section.id]);
          delete timers.current[section.id];
          setConflicts((prev) => ({ ...prev, [section.id]: { content, title: title ?? null } }));
        } else setError(e instanceof Error ? e.message : String(e));
      } finally { setDirty(false); }
    };
    const next = (saveChain.current[section.id] ?? Promise.resolve()).then(run);
    saveChain.current[section.id] = next;
    return next;
  }, [emit, repo]);

  const onContentChange = (section: ContentSectionRow, content: unknown) => {
    if (conflictIds.current.has(section.id)) return;
    const grading = gradingOf(content);
    const sharing = grading.mode === 'scale'
      ? sections.filter((s) => s.id !== section.id && !conflictIds.current.has(s.id) && gradingOf(s.content).mode === 'scale' && (gradingOf(s.content) as { scaleId: string }).scaleId === grading.scaleId)
      : [];
    const withSharedScale = (s: ContentSectionRow) => ({ ...s, content: { ...(s.content as object), grading } });
    const sharingIds = new Set(sharing.map((s) => s.id));
    setSections((prev) => emit(prev.map((s) => s.id === section.id ? { ...s, content } : sharingIds.has(s.id) ? withSharedScale(s) : s)));
    for (const other of [section, ...sharing]) {
      const next = other.id === section.id ? content : withSharedScale(other).content;
      clearTimeout(timers.current[other.id]);
      timers.current[other.id] = setTimeout(() => {
        delete timers.current[other.id];
        void persist({ ...other, content: next }, { content: next });
      }, 600);
    }
  };
  const onTitleChange = (section: ContentSectionRow, title: string | null) => {
    setSections((prev) => emit(prev.map((s) => s.id === section.id ? { ...s, title } : s)));
    void persist(section, { title });
  };

  const resolveConflict = async (id: string) => {
    conflictIds.current.delete(id);
    setConflicts((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id)));
    await load();
  };

  const addSection = async (type: ContentSectionType) => {
    const insertAt = insertAfterIdx !== null ? insertAfterIdx + 1 : sections.length;
    setAddingType(false); setInsertAfterIdx(null); setSavingNew(true);
    try {
      const section = await repo.upsert({ position: insertAt, type, title: null, content: defaultContent(type) });
      versions.current[section.id] = section.updated_at;
      setSections((prev) => {
        const next = [...prev];
        next.splice(insertAt, 0, section);
        return emit(next.map((s, i) => ({ ...s, position: i })));
      });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSavingNew(false); }
  };

  const deleteSection = async (id: string) => {
    if (!confirm('Supprimer cette section ?')) return;
    try {
      await repo.delete(id);
      setSections((prev) => emit(prev.filter((s) => s.id !== id)));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const moveSection = async (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    const reordered = next.map((s, i) => ({ ...s, position: i }));
    setSections(emit(reordered));
    try { await repo.reorder(reordered.map((s) => s.id)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div className="flex flex-col gap-4">
      {dirty && <div className="text-[11px] text-muted-foreground self-end">Enregistrement…</div>}
      {loading && <p className="text-muted-foreground text-[13px]">Chargement…</p>}
      {error && <p className="text-naia-error text-[13px]">{error}</p>}
      {!loading && sections.length === 0 && (
        <div className="text-center py-[40px] text-muted-foreground">
          <FileText size={32} weight="thin" className="mx-auto mb-2 opacity-40" />
          <p className="m-0 font-semibold">Document vide</p>
          <p className="mt-[6px] mb-0 text-[13px]">Ajoutez une section ou demandez à Naïa dans le chat.</p>
        </div>
      )}
      <DocumentScalesContext.Provider value={documentScales}>
      {sections.map((section, idx) => (
        <SectionRow key={section.id} section={section} documentId={documentId} idx={idx} total={sections.length}
          editable={editable && !conflicts[section.id]} structureEditable={structureEditable && !conflicts[section.id]}
          conflict={conflicts[section.id]} onReload={() => void resolveConflict(section.id)}
          onChange={(content) => onContentChange(section, content)}
          onTitle={(t) => onTitleChange(section, t)}
          onDelete={() => void deleteSection(section.id)}
          onMove={(dir) => void moveSection(idx, dir)}
          onAdd={editable && structureEditable ? () => { setInsertAfterIdx(idx); setAddingType(true); } : undefined}
          addDisabled={addingType || savingNew}
          showTypePicker={addingType && insertAfterIdx === idx}
          onPickType={(type) => void addSection(type)}
          onCancelPick={() => { setAddingType(false); setInsertAfterIdx(null); }}
        />
      ))}
      </DocumentScalesContext.Provider>
      {editable && structureEditable && sections.length === 0 && (
        addingType
          ? <TypePickerRow onSelect={(type) => void addSection(type)} onCancel={() => { setAddingType(false); setInsertAfterIdx(null); }} />
          : <AddHandle onAdd={() => { setInsertAfterIdx(null); setAddingType(true); }} disabled={savingNew} />
      )}
    </div>
  );
}
